import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { hasPermission } from "../../lib/permissions";
import DeleteWithReasonDialog from "../DeleteWithReasonDialog";
import { activeFundings, apiError, formatAmount, formatDay, toMinor } from "../../lib/finance";
import { DispositionBadge, PaymentBadge, ReceiptMissingBadge, WorkflowBadge } from "./FinanceBadges";
import { ReceiptPreviewDialog, receiptSource } from "./ReceiptPreview";
import RecordPaymentDialog from "./RecordPaymentDialog";
import CancelPaymentDialog, { payerName } from "./CancelPaymentDialog";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../ui/sheet";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { toast } from "sonner";
import { Download, FileText, History, Loader2, Pencil, Plus, Send } from "lucide-react";

const FIELD_LABELS = {
  title: "Libellé", description: "Description", incurred_at: "Date de commande ou de prestation", amount_minor: "Montant", currency: "Devise",
  category_id: "Catégorie", supplier: "Fournisseur", invoice_reference: "Référence de facture", beneficiary: "Bénéficiaire",
  preconstitution: "Antérieure à la société", project: "Projet", fundings: "Paiements", attachment_ids: "Justificatifs",
};
const KIND_LABELS = { created: "Création du brouillon", imported: "Import depuis un tableur", corrected: "Modification", submitted: "Soumission", payment_added: "Paiement enregistré", payment_cancelled: "Paiement annulé", disposition_recorded: "Décision consignée", generated: "Brouillon créé par un abonnement", supplier_linked: "Rattaché à une fiche fournisseur", created_from_purchase: "Brouillon créé depuis une demande d'achat" };

function changedFields(event) {
  if (event.kind !== "corrected" || !event.before || !event.after) return [];
  return Object.keys(FIELD_LABELS).filter((k) => JSON.stringify(event.before[k] ?? null) !== JSON.stringify(event.after[k] ?? null)
    // funding ids are regenerated on every correction: compare what a person reads, not technical ids
    && !(k === "fundings" && JSON.stringify((event.before[k] || []).map(({ id, ...f }) => f)) === JSON.stringify((event.after[k] || []).map(({ id, ...f }) => f))))
    .map((k) => FIELD_LABELS[k]);
}

function Row({ label, children }) {
  return (
    <div className="flex flex-col gap-0.5 py-2 border-b border-slate-100 last:border-0 sm:grid sm:grid-cols-[150px_minmax(0,1fr)] sm:gap-3">
      <dt className="text-xs font-medium text-slate-500">{label}</dt>
      <dd className="text-sm text-slate-900 break-words">{children}</dd>
    </div>
  );
}

const extension = (mime) => ({ "application/pdf": ".pdf", "image/jpeg": ".jpg", "image/png": ".png", "image/webp": ".webp" }[mime] || "");

export default function ExpenseDetailSheet({ expenseId, open, onOpenChange, canEdit, canDownload, onChanged }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [deleting, setDeleting] = useState(null);
  const [expense, setExpense] = useState(null);
  const [error, setError] = useState("");
  const [history, setHistory] = useState(null);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [paying, setPaying] = useState(false);
  const [cancelling, setCancelling] = useState(null); // the payment whose cancellation is being confirmed

  useEffect(() => {
    if (!open || !expenseId) return undefined;
    let cancelled = false;
    setExpense(null); setError(""); setHistory(null); setPreview(null); setPaying(false); setCancelling(null);
    api.get(`/finance/expenses/${expenseId}`)
      .then(({ data }) => { if (!cancelled) setExpense(data); })
      .catch((e) => { if (!cancelled) setError(apiError(e)); });
    return () => { cancelled = true; };
  }, [open, expenseId]);

  const loadHistory = async () => {
    try {
      const { data } = await api.get(`/finance/expenses/${expenseId}/history`);
      setHistory(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const download = async (file) => {
    try {
      const res = await api.get(`/finance/attachments/${file.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name || `justificatif${extension(file.mime)}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/finance/expenses/${expense.id}/submit`, { expected_version: expense.version });
      setExpense((old) => ({ ...old, ...data }));
      setConfirmSubmit(false);
      toast.success("Dépense soumise");
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const currency = expense?.currency;
  const remaining = expense ? toMinor(expense.outstanding, currency) : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg p-0 flex flex-col" data-testid="finance-expense-sheet">
        {!expense && !error && <div className="p-6 space-y-3"><Skeleton className="h-6 w-2/3" /><Skeleton className="h-4 w-1/3" /><Skeleton className="h-40 w-full" /></div>}
        {error && <div className="p-6 space-y-2"><SheetTitle>Dépense indisponible</SheetTitle><p className="text-sm text-slate-600">{error}</p></div>}
        {expense && (
          <>
            <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6 space-y-5">
              <SheetHeader className="text-left space-y-2 pr-8">
                <SheetTitle className="break-words">{expense.title}</SheetTitle>
                <SheetDescription asChild>
                  <div className="flex flex-wrap gap-1.5">
                    <WorkflowBadge status={expense.status} />
                    <PaymentBadge status={expense.payment_status} />
                    {expense.receipt_missing && <ReceiptMissingBadge />}
                  </div>
                </SheetDescription>
                <div className="text-2xl font-semibold text-slate-900 tabular-nums">{formatAmount(expense.amount, currency)}</div>
              </SheetHeader>

              <section aria-labelledby="fin-payments">
                <h3 id="fin-payments" className="text-sm font-semibold text-slate-900 mb-2">Paiements</h3>
                {!expense.fundings.length ? (
                  <p className="text-sm text-slate-600 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">Non payée : aucun paiement enregistré.</p>
                ) : (
                  <ul className="rounded-lg border border-slate-200 divide-y divide-slate-100">
                    {expense.fundings.map((f) => f.cancelled_at ? (
                      <li key={f.id} className="flex items-start justify-between gap-3 px-3 py-2 text-sm bg-slate-50" data-testid="finance-funding-cancelled">
                        <span className="min-w-0 text-slate-500">
                          <span className="flex items-center gap-2">
                            <span className="truncate line-through">{payerName(f)}</span>
                            <span className="shrink-0 rounded-full border border-slate-300 px-1.5 text-2xs font-medium text-slate-600 no-underline">Annulé</span>
                          </span>
                          <span className="block text-xs">{f.paid_at ? `Payé le ${formatDay(f.paid_at)}` : "Date de paiement manquante"} · annulé le {formatDay(f.cancelled_at)}</span>
                          <span className="block text-xs break-words">Motif : {f.cancel_reason}</span>
                        </span>
                        <span className="tabular-nums whitespace-nowrap text-slate-400 line-through">{formatAmount(f.amount, currency)}</span>
                      </li>
                    ) : (
                      <li key={f.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm" data-testid="finance-funding-line">
                        <span className="min-w-0">
                          <span className="block truncate text-slate-900">{payerName(f)}</span>
                          <span className="flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
                            {f.paid_at ? `Payé le ${formatDay(f.paid_at)}` : "Date de paiement manquante"}
                            {expense.status === "submitted" && f.payer_type !== "company" && <DispositionBadge status={f.disposition?.status || "a_decider"} />}
                          </span>
                        </span>
                        <span className="flex items-center gap-1 shrink-0">
                          <span className="tabular-nums font-medium whitespace-nowrap">{formatAmount(f.amount, currency)}</span>
                          {expense.status === "submitted" && canEdit && (
                            <Button type="button" variant="ghost" size="sm" className="text-slate-500 hover:text-red-700" onClick={() => setCancelling(f)}
                              aria-label={`Annuler le paiement de ${payerName(f)}`} data-testid="finance-cancel-payment">
                              Annuler
                            </Button>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                {remaining !== null && remaining > 0 && activeFundings(expense).length > 0 && (
                  <p className="text-sm text-slate-600 mt-2">Reste à payer : <strong className="tabular-nums">{formatAmount(expense.outstanding, currency)}</strong></p>
                )}
                {expense.status === "submitted" && canEdit && remaining !== null && remaining > 0 && (
                  <Button type="button" variant="outline" size="sm" className="mt-2" onClick={() => setPaying(true)} data-testid="finance-record-payment">
                    <Plus size={14} className="mr-1" /> Enregistrer un paiement
                  </Button>
                )}
              </section>

              <section aria-labelledby="fin-receipts">
                <h3 id="fin-receipts" className="text-sm font-semibold text-slate-900 mb-2">Justificatifs</h3>
                {expense.receipt_missing ? (
                  <p className="text-sm text-red-700 rounded-lg bg-red-50 border border-red-200 px-3 py-2">
                    Aucun justificatif. {expense.status === "draft" ? "Ajoutez-en un en modifiant la dépense : il est obligatoire pour la soumettre." : ""}
                  </p>
                ) : (
                  <ul className="rounded-lg border border-slate-200 divide-y divide-slate-100">
                    {(expense.attachments || []).map((file, i) => (
                      <li key={file.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                        <FileText size={16} className="text-slate-400 shrink-0" aria-hidden="true" />
                        {canDownload && !file.unavailable ? (
                          <button type="button" className="min-w-0 flex-1 truncate text-left text-slate-900 hover:text-jamora hover:underline"
                            onClick={() => setPreview({ source: receiptSource.attachment(file.id), name: file.name || `Pièce ${i + 1}` })}
                            aria-label={`Voir ${file.name || `la pièce ${i + 1}`}`} data-testid="finance-receipt-open">
                            {file.name || `Pièce ${i + 1}`}
                          </button>
                        ) : (
                          <span className="min-w-0 flex-1 truncate">{file.unavailable ? `Pièce ${i + 1} (indisponible)` : file.name || `Pièce ${i + 1}`}</span>
                        )}
                        {canDownload && !file.unavailable && (
                          <Button type="button" variant="ghost" size="sm" onClick={() => download(file)} aria-label={`Télécharger ${file.name || `la pièce ${i + 1}`}`}>
                            <Download size={14} />
                          </Button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section aria-labelledby="fin-info">
                <h3 id="fin-info" className="text-sm font-semibold text-slate-900 mb-1">Informations</h3>
                <dl>
                  <Row label="Catégorie">{expense.category_name}</Row>
                  <Row label="Fournisseur">{expense.supplier ? <>{expense.supplier}{expense.supplier_id && <span className="ml-2 text-xs text-slate-500" data-testid="expense-supplier-linked">· fiche liée</span>}</> : <span className="text-slate-400">Non renseigné</span>}</Row>
                  {expense.incurred_at && <Row label="Commande / prestation">{formatDay(expense.incurred_at)}</Row>}
                  {expense.invoice_reference && <Row label="Référence de facture">{expense.invoice_reference}</Row>}
                  {expense.project && <Row label="Projet">{expense.project}</Row>}
                  {expense.beneficiary && <Row label="Bénéficiaire">{expense.beneficiary}</Row>}
                  {expense.preconstitution && <Row label="Période">Antérieure à la création de la société</Row>}
                  {expense.description && <Row label="Description"><span className="whitespace-pre-line">{expense.description}</span></Row>}
                  <Row label="Saisie le">{formatDay(expense.created_at)}</Row>
                  {expense.created_by_name && <Row label="Saisie par">{expense.created_by_name}</Row>}
                  {expense.submitted_at && <Row label="Soumise le">{formatDay(expense.submitted_at)}</Row>}
                </dl>
              </section>

              <section aria-labelledby="fin-history">
                {history === null ? (
                  <Button type="button" variant="ghost" size="sm" className="-ml-2 text-slate-600" onClick={loadHistory}><History size={14} className="mr-1.5" /> Voir l'historique</Button>
                ) : (
                  <>
                    <h3 id="fin-history" className="text-sm font-semibold text-slate-900 mb-2">Historique</h3>
                    <ol className="space-y-2">
                      {history.map((event) => {
                        const changed = changedFields(event);
                        return (
                          <li key={event.id} className="text-sm border-l-2 border-slate-200 pl-3">
                            <span className="text-slate-900">{KIND_LABELS[event.kind] || event.kind}</span>
                            <span className="text-slate-500"> · {new Date(event.at).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" })}</span>
                            {changed.length > 0 && <span className="block text-xs text-slate-500">Modifié : {changed.join(", ")}</span>}
                          </li>
                        );
                      })}
                    </ol>
                  </>
                )}
              </section>
            </div>

            {expense.status === "draft" && canEdit && (
              <div className="border-t border-slate-200 p-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 bg-white">
                {expense.source_system === "manual" && hasPermission(user, "finance:delete") && (
                  <Button type="button" variant="outline" className="text-red-700 sm:mr-auto" onClick={() => setDeleting(expense)} data-testid="finance-delete-expense">
                    Supprimer…
                  </Button>
                )}
                <Button type="button" variant="outline" onClick={() => navigate(`/finance/depenses/${expense.id}/modifier`)} data-testid="finance-edit-expense">
                  <Pencil size={14} className="mr-1.5" /> Modifier
                </Button>
                <Button type="button" onClick={() => setConfirmSubmit(true)} disabled={expense.receipt_missing}
                  data-testid="finance-submit-expense"
                  title={expense.receipt_missing ? "Ajoutez un justificatif avant de soumettre" : undefined}>
                  <Send size={14} className="mr-1.5" /> Soumettre
                </Button>
              </div>
            )}
          </>
        )}
      </SheetContent>
      <DeleteWithReasonDialog
        target={deleting} testId="delete-expense" onClose={() => setDeleting(null)} confirmLabel="Supprimer définitivement"
        title={`Supprimer « ${deleting?.title || ""} » ?`}
        description="Ce brouillon n'a jamais été soumis : il est supprimé pour de bon, avec son motif gardé dans l'historique. Ses justificatifs sont conservés."
        onConfirm={async (reason) => {
          const target = deleting;
          try {
            await api.delete(`/finance/expenses/${target.id}`, { data: { reason } });
            toast.success("Brouillon supprimé");
            setDeleting(null);
            onOpenChange(false);
            onChanged?.();
          } catch (e) {
            setDeleting(null);
            toast.error(apiError(e, "Impossible de supprimer ce brouillon."));
          }
        }}
      />
      <AlertDialog open={confirmSubmit} onOpenChange={setConfirmSubmit}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Soumettre « {expense?.title} » ?</AlertDialogTitle>
            <AlertDialogDescription>
              Une fois soumise, la dépense ne peut plus être modifiée. La soumission ne vaut ni approbation ni remboursement.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); submit(); }} disabled={submitting} className="bg-jamora hover:bg-jamora-hover" data-testid="finance-confirm-submit">
              {submitting && <Loader2 size={14} className="animate-spin mr-1.5" />} Soumettre
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <ReceiptPreviewDialog receipt={preview} onClose={() => setPreview(null)} />
      {expense && (
        <RecordPaymentDialog expense={expense} open={paying} onOpenChange={setPaying}
          onRecorded={(data) => { setExpense((current) => ({ ...current, ...data })); setHistory(null); onChanged?.(); }} />
      )}
      {expense && (
        <CancelPaymentDialog expense={expense} funding={cancelling} onClose={() => setCancelling(null)}
          onCancelled={(data) => { setExpense((current) => ({ ...current, ...data })); setHistory(null); onChanged?.(); }} />
      )}
    </Sheet>
  );
}
